import React, { type ReactNode, useRef, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import useScrollDirection from '@/app/common/hooks/useScrollDirection.ts';
import { StickyOnScrollUpWrapper } from '@/app/common/components/StickyOnScrollUp/styled.ts';

export type StickyOnScrollUpProps = {
  topOffset: number;
  children: ReactNode;
};

const StickyOnScrollUp = ({ topOffset, children }: StickyOnScrollUpProps) => {
  const scrollDirection = useScrollDirection();
  const { ref: sentinelRef, inView } = useInView({ threshold: 0 });
  const wasRevealed = useRef(false);

  const isOut = !inView;
  const isSticky = isOut || scrollDirection === 'up';
  const isHidden = isOut && scrollDirection === 'down';
  const showShadow = isOut && scrollDirection === 'up';

  useEffect(() => {
    if (isOut && scrollDirection === 'up') wasRevealed.current = true;
    if (inView && scrollDirection === null) wasRevealed.current = false;
  }, [isOut, inView, scrollDirection]);

  // Animate only after the bar has been revealed; avoids flash on first exit
  const animate = wasRevealed.current || scrollDirection === 'up';

  return (
    <>
      <div ref={sentinelRef} style={{ height: 0, width: 0 }} />
      <StickyOnScrollUpWrapper
        $isOut={isSticky}
        $hidden={isHidden}
        $showShadow={showShadow}
        $animate={animate}
        $top={topOffset}
      >
        {children}
      </StickyOnScrollUpWrapper>
    </>
  );
};

export default StickyOnScrollUp;
